import { tripData } from '../../utils/helpers'
import { zeroPadding } from '../../utils/time-utils'
import { createVertex } from '../../utils/helpers'

/**
 * Build Navitime API params and url for route requests.
 * @param {string} startTime ISO date time (YYYY-MM-DDTHH:mm:ss)
 * @param {{coord:{lon:number,lat:number},node?:string,name?:string}} start
 * @param {{coord:{lon:number,lat:number},node?:string,name?:string}} goal
 * @param {Array<{lat:number,lon:number,name?:string}>} wayPoints
 * @returns {{params:Object, routeUrl:string}}
 */
function applyRegulationParams(params, alpineComponent) {
  if (!alpineComponent?.regulation?.isHeavyVehicle) return
  const regulation = {
    'regulation-type': alpineComponent.regulation.type,
    'toll-type': alpineComponent.regulation.tollType
  }
  if (alpineComponent.regulation.dangerousGoods) {
    regulation['dangerous-goods'] = 'use'
  }
  params.regulation = JSON.stringify(regulation)
}

function navitimeRouteRequest(params) {
  const request = {
    start: JSON.parse(params.start),
    goal: JSON.parse(params.goal),
    startTime: params.start_time,
    regulation: params.regulation ? JSON.parse(params.regulation) : null
  }
  if (params.via) {
    request.via = JSON.parse(params.via)
  }
  return request
}

async function fetchCachedNavitimeRoute(params) {
  const token = document.querySelector('input[name="csrf-token"]').value
  const response = await fetch('/solution/navitime/route', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({requestStr: JSON.stringify(navitimeRouteRequest(params)), 'csrf-token': token})
  })
  if (!response.ok) {
    throw new Error(`Local Navitime route cache failed: ${response.status}`)
  }
  return response.json()
}

/**
 * Fetch driving duration (ms) between two visits via Navitime route API.
 * @returns {Promise<number|null>} Duration in milliseconds, or null if unavailable.
 */
export function fetchNavitimeDriveDuration(from, to, departureTimeMillis, alpineComponent) {
  if (!from?.lat || !from?.lon || !to?.lat || !to?.lon) {
    return Promise.resolve(null)
  }

  const hour = Math.floor(departureTimeMillis / (1000 * 60 * 60))
  const minute = Math.floor((departureTimeMillis % (1000 * 60 * 60)) / (1000 * 60))
  const dateTime = alpineComponent.date + `T${zeroPadding(hour, 2)}:${zeroPadding(minute, 2)}:00`
  const start = { coord: { lon: from.lon, lat: from.lat }, node: '', name: from.name }
  const goal = { coord: { lon: to.lon, lat: to.lat }, node: '', name: to.name }
  const { params } = buildNavitimeRouteParams(dateTime, start, goal, [])
  applyRegulationParams(params, alpineComponent)

  return fetchCachedNavitimeRoute(params)
    .then((json) => json.durationMillis)
    .catch((error) => {
      console.warn('Navitime route failed:', error.message)
      return null
    })
}

function buildNavitimeRouteParams(startTime, start, goal, wayPoints) {
  const params = {
    start: JSON.stringify(createVertex(start)),
    goal: JSON.stringify(createVertex(goal)),
    start_time: startTime
  }
  if (wayPoints && wayPoints.length > 0) {
    params.via = JSON.stringify(wayPoints)
  }
  return { params }
}

/** @deprecated Use buildNavitimeRouteParams via cached backend proxy. */
export function getNavitimeAPIData(startTime, start, goal, wayPoints) {
  return buildNavitimeRouteParams(startTime, start, goal, wayPoints)
}

/**
 * Perform route search against Navitime API, render on mapscript, and update Alpine component.
 * @param {Object} params
 * @param {string} routeUrl
 * @param {number} startTime Start time in ms
 * @param {Object} alpineComponent Alpine component to update with totalTime and visits
 */
export function searchRoute(params, startTime, alpineComponent) {
  fetchCachedNavitimeRoute(params)
    .then(function (cachedRoute) {
      const json = cachedRoute.route
      if (!json?.items?.length) {
        console.log('no data')
        return
      }

      const item = json.items[0]
      const { travelTime, visits } = tripData(item.sections, startTime)
      alpineComponent.totalTime = travelTime
      alpineComponent.visits = visits

      if (!item.shapes) {
        console.warn('Navitime route has no shapes; cannot render map path')
        return
      }

      const figure = new mapscript.value.GeoJsonFigureCondition(item.shapes, {
        isRouteShape: true,
        showRouteArrow: true,
        coordUnit: 'degree',
        polyline: {
          inline: { color: new mapscript.value.Color(0, 1, 0, 1), width: 7 },
          outline: { color: new mapscript.value.Color(1, 0, 0, 1), width: 7 }
        }
      })
      window.routePath.push(figure)
      const [lng1, lat1, lng2, lat2] = item.shapes.bbox
      const rect = mapscript.util.locationsToLatLngRect([
        new mapscript.value.LatLng(lat1, lng1),
        new mapscript.value.LatLng(lat2, lng2)
      ])
      window.map.moveBasedOnLatLngRect(rect, true)
      window.map.addGeoJsonFigure(figure)
    })
    .catch(function (error) {
      console.log('Navitime route failed: ' + error.message)
    })
}

/**
 * Add a specific path for DRIVING segment between two visits.
 */
export function addSpecificPath(from, to, alpineComponent) {
  const hour = Math.floor(from.departureTime / (1000 * 60 * 60))
  const minute = Math.floor((from.departureTime % (1000 * 60 * 60)) / (1000 * 60))
  const dateTime = alpineComponent.date + `T${zeroPadding(hour, 2)}:${zeroPadding(minute, 2)}:00`

  const start = { coord: { lon: from.lon, lat: from.lat }, node: '', name: from.name }
  const goal = { coord: { lon: to.lon, lat: to.lat }, node: '', name: to.name }
  const { params } = buildNavitimeRouteParams(dateTime, start, goal, [])

  applyRegulationParams(params, alpineComponent)

  searchRoute(params, from.departureTime, alpineComponent)
}

/**
 * Clear all markers, info windows, and route paths from mapscript
 */
export function deleteMarkersAndPaths() {
  for (const marker of window.markers) {
    window.map.removeGLMarker(marker)
  }
  window.markers = []

  for (const infoWindow of window.infoWindows) {
    window.map.removeInfoWindow(infoWindow)
  }
  window.infoWindows = []

  for (const path of window.routePath) {
    window.map.removeGeoJsonFigure(path)
  }
  window.routePath = []
}

export default { getNavitimeAPIData, searchRoute, addSpecificPath, deleteMarkersAndPaths, fetchNavitimeDriveDuration }
